import {
  queryOptions,
  UnusedSkipTokenOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'

import { useToaster } from '@forto-dev/phoenix-ui-core'

import Client, { ErrorOptions } from '@/api/client'
import { getConfig } from '@/app/config'
import {
  BookingDTO,
  BookingPartiesResponseDTO,
  CreateBookingDTO,
  CreateBookingResultDTO,
  GetHistoryDTO,
} from '@/clients/booking'
import { createEntityDocument } from '@/features/documents'
import { useLocalisation } from '@/utils/localisation'

import { sanitizeOriginLocalHandlingService } from './sanitizeOriginLocalHandlingService'
import { pollBookingQuery } from '../features/overview/utils/pollBookingQuery'
import { BookingDocument } from '../features/wizard/types/BookingWizardFormState'

const client = new Client(getConfig().API_ENDPOINT ?? '')

const basePath = '/api/booking-v2'

export const getBooking = async (bookingId: string, options?: ErrorOptions): Promise<BookingDTO> => {
  return await client.get<BookingDTO>(`${basePath}/bookings/${bookingId}`, options)
}

export const getParties = async (bookingId: string) => {
  return client.get<BookingPartiesResponseDTO>(`${basePath}/bookings/${bookingId}/parties`)
}

export const createBooking = (booking: CreateBookingDTO): Promise<CreateBookingResultDTO> => {
  const sanitizedBooking = sanitizeOriginLocalHandlingService(booking)
  return client.post<CreateBookingResultDTO>(`${basePath}/bookings`, sanitizedBooking)
}

export const authorizeBooking = (bookingId: string, comment: string) => {
  return client.post<void>(`${basePath}/bookings/${bookingId}/authorize`, { comment })
}

export const rejectBooking = (bookingId: string, comment: string) => {
  return client.post<void>(`${basePath}/bookings/${bookingId}/reject`, { comment })
}

type BookingHistoryItem = GetHistoryDTO['items'][number]
type BookingHistoryParams = {
  type: BookingHistoryItem['type']
}
export const getBookingHistory = (id: string, params?: BookingHistoryParams) => {
  return client.get<GetHistoryDTO>(`${basePath}/bookings/${id}/history`, { params })
}

const bookingQueryKey = (bookingId: string) => ['bookings', bookingId]

type BookingQueryOptions = Omit<UnusedSkipTokenOptions<BookingDTO>, 'queryKey'>
export const bookingQueryOptions = (bookingId: string, options: BookingQueryOptions = {}) =>
  queryOptions({
    queryKey: bookingQueryKey(bookingId),
    queryFn: async () => getBooking(bookingId),
    ...options,
  })

export const useBooking = (bookingId: string, options: BookingQueryOptions = {}) => {
  return useSuspenseQuery(bookingQueryOptions(bookingId, options))
}

export const useBookingSuspenseQueryWithPolling = (bookingId: string) => {
  const queryResponse = useBooking(bookingId, {
    refetchInterval: ({ state }) => {
      return pollBookingQuery({
        pollCount: state.dataUpdateCount,
      })
    },
  })

  return queryResponse
}

export const getBookingHistoryQueryCacheKey = (bookingID: string) => ['bookingsHistory', bookingID]
export const useBookingHistoryQuery = (bookingID: string) => {
  return useQuery({
    queryKey: getBookingHistoryQueryCacheKey(bookingID),
    queryFn: () => getBookingHistory(bookingID),
    initialData: {
      items: [],
      totalItems: 0,
    },
  })
}

type BookingAuthPayload = {
  id: string
  comment: string
}

type UseBookingAuthMutationsPayload = {
  mutationFn: (payload: BookingAuthPayload) => Promise<void>
  successTitle: string
  onSuccess: () => void
  onError: () => void
  errorTitle: string
}

export const useBookingAuthMutations = ({
  mutationFn,
  successTitle,
  errorTitle,
  onSuccess,
  onError,
}: UseBookingAuthMutationsPayload) => {
  const toaster = useToaster()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: BookingAuthPayload) => {
      await mutationFn(payload)
      return payload
    },
    onSuccess: (payload) => {
      toaster({ title: successTitle })
      queryClient.invalidateQueries({
        queryKey: bookingQueryKey(payload.id),
      })
      queryClient.invalidateQueries({
        queryKey: getBookingHistoryQueryCacheKey(payload.id),
      })
      onSuccess()
    },
    onError: () => {
      toaster({ title: errorTitle, variant: 'negative' })
      onError()
    },
  })
}

export const useAuthorizeBookingMutation = (onSuccess: () => void, onError?: () => void) => {
  const { translate } = useLocalisation()
  return useBookingAuthMutations({
    mutationFn: (payload) => authorizeBooking(payload.id, payload.comment),
    successTitle: translate('booking.overview.auth.authorize.request_success'),
    errorTitle: translate('booking.overview.auth.authorize.request_error'),
    onSuccess,
    onError: () => {
      onError?.()
    },
  })
}

export const useRejectBookingMutation = (onSuccess: () => void, onError?: () => void) => {
  const { translate } = useLocalisation()
  return useBookingAuthMutations({
    mutationFn: (payload) => rejectBooking(payload.id, payload.comment),
    successTitle: translate('booking.overview.auth.reject.request_success'),
    errorTitle: translate('booking.overview.auth.reject.request_error'),
    onSuccess,
    onError: () => {
      onError?.()
    },
  })
}

// Test shouldShowAuthActions and move flags to a function

export const usePartiesQuery = (bookingId: string) => {
  return useSuspenseQuery({
    queryKey: ['booking', 'parties', bookingId],
    queryFn: async () => getParties(bookingId),
  })
}

export const useCreateBookingMutation = () => {
  return useMutation({
    mutationFn: (booking: CreateBookingDTO) => createBooking(booking),
  })
}

type UploadBookingDocumentsMutationPayload = {
  files: BookingDocument[]
  bookingId: string
}

export const useUploadBookingDocumentsMutation = (customerId: string) => {
  return useMutation({
    mutationFn: async ({ files, bookingId }: UploadBookingDocumentsMutationPayload) => {
      const promises = files.map((file) => {
        return createEntityDocument({
          entityId: bookingId,
          data: {
            file: file.file,
            state: 'published',
            mimeType: file.file.type,
            customerID: customerId,
            source: 'customer',
            documentTypeCode: file.role,
          },
        })
      })

      await Promise.all(promises)
    },
  })
}
